"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import InteractiveIcon from "@/components/drops/media/InteractiveIcon";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Link from "next/link";
import { useState } from "react";
interface LeadingCardProps {
  readonly drop: ExtendedDrop;
  readonly rank: number;
}
const getRankLabelClass = (rank: number) => {
  if (rank === 1) return "tw-text-[#E8D48A]";
  if (rank === 2) return "tw-text-[#DDDDDD]";
  if (rank === 3) return "tw-text-[#CD7F32]";
  return "tw-text-white/60";
};
export const LeadingCard = t0 => {
  const $ = _c(52);
  const {
    drop,
    rank
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  const isHtml = media?.mime_type === "text/html";
  const shouldGate = hasTouchScreen && isHtml;
  const [manuallyEnabled, setManuallyEnabled] = useState(false);
  const interactiveEnabled = !shouldGate || manuallyEnabled;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t1;
  if ($[0] !== rank) {
    t1 = getRankLabelClass(rank);
    $[0] = rank;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rankLabelClass = t1;
  let t2;
  bb0: {
    if (!media) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-iron-950"><span className="tw-text-sm tw-text-white/40">No image</span></div>;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb0;
    }
    if (shouldGate && !interactiveEnabled) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = () => setManuallyEnabled(true);
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-iron-950/30"><button type="button" onClick={t3} className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-transparent" aria-label="Load interactive media"><span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-white/20 tw-bg-iron-950/90 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-shadow-[0_16px_40px_rgba(0,0,0,0.65)] tw-ring-1 tw-ring-white/10 tw-transition hover:tw-bg-iron-900"><InteractiveIcon className="tw-size-4 tw-flex-shrink-0" />Tap to load</span></button></div>;
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      t2 = t4;
      break bb0;
    }
    let t3;
    if ($[5] !== media.mime_type || $[6] !== media.url || $[7] !== mediaImageScale) {
      t3 = <MediaDisplay media_mime_type={media.mime_type} media_url={media.url} disableMediaInteraction={true} imageScale={mediaImageScale} />;
      $[5] = media.mime_type;
      $[6] = media.url;
      $[7] = mediaImageScale;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t3;
  }
  const mediaContent = t2;
  const t3 = `tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide ${rankLabelClass}`;
  const t4 = rank === 1 ? "LEADING" : `CURRENT TOP ${rank}`;
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== drop.rating) {
    t6 = formatNumberWithCommas(drop.rating);
    $[12] = drop.rating;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <span className="tw-text-white/80">{t6}</span>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="tw-text-white/50"> TDH</span>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7) {
    t9 = <span className="tw-font-mono tw-text-xs">{t7}{t8}</span>;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t5}{t9}</div>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== mediaContent) {
    t11 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{mediaContent}</div></div>;
    $[22] = mediaContent;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = media?.mime_type;
  let t13;
  if ($[24] !== drop.id || $[25] !== t12) {
    t13 = <MediaTypeBadge mimeType={t12} dropId={drop.id} size="sm" />;
    $[24] = drop.id;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== drop.id || $[28] !== drop.wave.id) {
    t14 = getWaveRoute({
      waveId: drop.wave.id,
      extraParams: {
        drop: drop.id
      },
      isDirectMessage: false,
      isApp: false
    });
    $[27] = drop.id;
    $[28] = drop.wave.id;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== title) {
    t15 = <Link href={t14} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-iron-200 tw-no-underline tw-transition-colors group-hover:tw-text-white @lg:tw-line-clamp-1">{title}</Link>;
    $[30] = t14;
    $[31] = title;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-gap-2">{t13}{t15}</div>;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = `/${author.handle ?? author.primary_address}`;
  const t18 = author.handle ?? "Artist";
  let t19;
  if ($[36] !== author.pfp || $[37] !== t18) {
    t19 = <ProfileAvatar pfpUrl={author.pfp} alt={t18} size={ProfileBadgeSize.SMALL} />;
    $[36] = author.pfp;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = author.handle ?? "Anonymous";
  let t21;
  if ($[39] !== t20) {
    t21 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t20}</span>;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t17 || $[42] !== t19 || $[43] !== t21) {
    t22 = <Link href={t17} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t19}{t21}</Link>;
    $[41] = t17;
    $[42] = t19;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t16 || $[46] !== t22) {
    t23 = <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/10 tw-px-3 tw-py-3 sm:tw-px-4">{t16}{t22}</div>;
    $[45] = t16;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t23) {
    t24 = <div className="tw-group tw-flex tw-flex-col tw-text-left tw-transition-all tw-duration-500"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-transition-colors group-hover:tw-border-white/10">{t10}{t11}{t23}</div></div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  return t24;
};
function _temp(m) {
  return m.data_key === "title";
}